import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useUnitManagement } from '@/hooks/useUnitManagement';
import { useOfflineCache } from '@/hooks/useOfflineCache';
import { useRealtimeSync, addRecentlyCreatedItem } from '@/hooks/useRealtimeSync';
import type {
  D2DArea, 
  D2DStreet, 
  D2DHouse, 
  D2DUnit, 
  D2DNote, 
  D2DUserPreferences,
  D2DTag,
  D2DCreateStreetData,
  D2DCreateHouseData,
  D2DCreateUnitData
} from '@/types/d2d';

export const useD2DData = () => {
  const { user } = useAuth();
  const { isOnline, queueAction, queueLength, isSyncing } = useOfflineSync(user?.id);
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState<D2DArea[]>([]);
  const [streets, setStreets] = useState<D2DStreet[]>([]);
  const [houses, setHouses] = useState<D2DHouse[]>([]);
  const [preferences, setPreferences] = useState<D2DUserPreferences | null>(null);
  
  // Offline cache for area data
  const {
    isCaching,
    cachedAreaIds,
    cacheArea,
    clearCache,
    getCachedStreets,
    getCachedHouses,
    getCachedUnits,
    getCachedAreaUnits,
    updateUnitOffline,
    checkAreaCached
  } = useOfflineCache({ userId: user?.id, isOnline });
  
  // Use dedicated unit management hook for isolated, reliable unit operations
  const { 
    units, 
    setUnits, 
    createUnit: createUnitOptimistic,
    createUnitsBatch: createUnitsBatchOptimistic,
    addNote: addNoteOptimistic, 
    deleteNote: deleteNoteOptimistic,
    isHotLead 
  } = useUnitManagement({ userId: user?.id, isOnline });

  // Track current navigation for realtime sync
  const [currentAreaId, setCurrentAreaId] = useState<string | null>(null);
  const [currentStreetId, setCurrentStreetId] = useState<string | null>(null);
  const [currentHouseId, setCurrentHouseId] = useState<string | null>(null);

  // Realtime sync callbacks for collaborative editing
  const handleStreetInserted = useCallback((newStreet: D2DStreet) => {
    setStreets(prev => {
      if (prev.some(s => s.id === newStreet.id)) return prev;
      return [...prev, { ...newStreet, tag_counts: { BK: 0, NA: 0, HOT: 0, KT: 0, KI: 0, NO: 0, WIN: 0 }, total_units: 0 }];
    });
  }, []);

  const handleStreetUpdated = useCallback((updatedStreet: D2DStreet) => {
    setStreets(prev => prev.map(s => 
      s.id === updatedStreet.id 
        ? { ...s, ...updatedStreet }
        : s
    ));
  }, []);

  const handleHouseInserted = useCallback((newHouse: D2DHouse) => {
    setHouses(prev => {
      if (prev.some(h => h.id === newHouse.id)) return prev;
      return [...prev, { ...newHouse, units: [], tag_summary: '' }];
    });
  }, []);

  const handleHouseUpdated = useCallback((updatedHouse: D2DHouse) => {
    setHouses(prev => prev.map(h => 
      h.id === updatedHouse.id 
        ? { ...h, ...updatedHouse }
        : h
    ));
  }, []);

  const handleUnitInserted = useCallback((newUnit: D2DUnit) => {
    setUnits(prev => {
      if (prev.some(u => u.id === newUnit.id)) return prev;
      return [...prev, { ...newUnit, notes: [], notes_count: 0, is_hot: isHotLead(newUnit) }];
    });
  }, [isHotLead]);

  const handleUnitUpdated = useCallback((updatedUnit: D2DUnit) => {
    setUnits(prev => prev.map(u => 
      u.id === updatedUnit.id 
        ? { ...u, ...updatedUnit, is_hot: isHotLead(updatedUnit) }
        : u
    ));
  }, [isHotLead]);

  // Enable realtime sync for collaborative editing
  useRealtimeSync({
    userId: user?.id,
    currentAreaId,
    currentStreetId,
    currentHouseId,
    onStreetInserted: handleStreetInserted,
    onStreetUpdated: handleStreetUpdated,
    onHouseInserted: handleHouseInserted,
    onHouseUpdated: handleHouseUpdated,
    onUnitInserted: handleUnitInserted,
    onUnitUpdated: handleUnitUpdated,
  });

  // Note: Realtime subscriptions for areas are handled by D2DAreaList component

  // Load user preferences
  const loadPreferences = async () => {
    if (!user?.id) return;
    
    const { data, error } = await supabase
      .from('d2d_user_preferences')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      console.error('Error loading preferences:', error);
      return;
    }

    setPreferences(data);
  };

  // Save user preferences
  const savePreferences = async (updates: Partial<D2DUserPreferences>) => {
    if (!user?.id) return;

    console.log('💾 Attempting to save preferences:', updates);

    // Try update first, then insert if no rows affected
    const { error: updateError, count } = await supabase
      .from('d2d_user_preferences')
      .update(updates)
      .eq('user_id', user.id);

    if (updateError) {
      console.error('❌ Error updating preferences:', updateError);
      return;
    }

    console.log('📊 Update result - affected rows:', count);

    // If no rows were updated, create new preferences
    if (count === 0) {
      console.log('🆕 No existing preferences found, creating new ones...');
      const { error: insertError } = await supabase
        .from('d2d_user_preferences')
        .insert({
          user_id: user.id,
          ...updates,
        });

      if (insertError) {
        console.error('❌ Error creating preferences:', insertError);
        return;
      }
      console.log('✅ New preferences created successfully');
    } else {
      console.log('✅ Preferences updated successfully');
    }

    // Update local state directly instead of reloading to prevent loops
    setPreferences(prev => prev ? { ...prev, ...updates } : { 
      id: crypto.randomUUID(), 
      user_id: user.id, 
      created_at: new Date().toISOString(), 
      updated_at: new Date().toISOString(),
      last_navigation_level: 'areas',
      last_area_id: null,
      last_street_id: null,
      last_house_id: null,
      last_plz: null,
      ...updates 
    });
  };

  // Load areas (user-specific and shared) - OPTIMIZED with parallel queries
  const loadAreas = async () => {
    if (!user?.id) return;
    
    // Prevent multiple concurrent loads
    if (loading) {
      return;
    }
    
    setLoading(true);
    try {
      // Run both queries in parallel instead of sequentially
      const [ownAreasResult, sharedAreaIdsResult] = await Promise.all([
        supabase
          .from('d2d_areas')
          .select('*')
          .eq('created_by', user.id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('d2d_area_shares')
          .select('area_id')
          .eq('shared_with', user.id)
      ]);

      if (ownAreasResult.error) throw ownAreasResult.error;
      if (sharedAreaIdsResult.error) throw sharedAreaIdsResult.error;

      const ownAreas = ownAreasResult.data || [];
      const sharedAreaIds = sharedAreaIdsResult.data || [];

      let sharedAreas: D2DArea[] = [];
      if (sharedAreaIds.length > 0) {
        const areaIds = sharedAreaIds.map(s => s.area_id);
        const { data: sharedData, error: sharedAreasError } = await supabase
          .from('d2d_areas')
          .select('*')
          .in('id', areaIds)
          .order('updated_at', { ascending: false });

        if (sharedAreasError) throw sharedAreasError;
        sharedAreas = sharedData || [];
      }

      // Combine own and shared areas, avoiding duplicates
      const allAreas = [...ownAreas, ...sharedAreas];
      const uniqueAreas = Array.from(new Map(allAreas.map(area => [area.id, area])).values());
      
      setAreas(uniqueAreas);
    } catch (error) {
      console.error('Error loading areas:', error);
      
      try {
        toast({
          title: "Fehler",
          description: "Gebiete konnten nicht geladen werden.",
          variant: "destructive",
        });
      } catch (toastError) {
        console.error('Error showing toast:', toastError);
      }
      
      setAreas([]);
    } finally {
      setLoading(false);
    }
  };

  // Load streets for area (including shared areas)
  const loadStreets = async (areaId: string) => {
    if (!user?.id) return;
    
    // Set current area ID for realtime sync
    setCurrentAreaId(areaId);
    setCurrentStreetId(null);
    setCurrentHouseId(null);
    
    setLoading(true);
    try {
      console.log('📍 Loading streets for area:', areaId);
      
      // If offline, try to use cached data
      if (!isOnline) {
        const cachedStreets = await getCachedStreets(areaId);
        if (cachedStreets.length > 0) {
          console.log('📦 Using cached streets:', cachedStreets.length);
          setStreets(cachedStreets);
          setLoading(false);
          return;
        }
      }
      
      // First load just the street info - fast query
      const { data: streetsData, error: streetsError } = await supabase
        .from('d2d_streets')
        .select('id, name, plz, area_id, is_active, created_at, updated_at, user_id, organization_id')
        .eq('area_id', areaId)
        .eq('is_active', true)
        .order('name');

      if (streetsError) throw streetsError;

      if (!streetsData || streetsData.length === 0) {
        console.log('✅ No streets found for area');
        setStreets([]);
        return;
      }

      const streetIds = streetsData.map(s => s.id);
      
      // Then load houses with their street_id
      const { data: housesData, error: housesError } = await supabase
        .from('d2d_houses')
        .select('id, street_id')
        .in('street_id', streetIds);

      if (housesError) {
        console.error('Error loading houses:', housesError);
        // Set streets without counts
        setStreets(streetsData.map(s => ({
          ...s,
          tag_counts: { BK: 0, NA: 0, HOT: 0, KT: 0, KI: 0, NO: 0, WIN: 0 },
          total_units: 0
        })));
        return;
      }

      const houseIds = (housesData || []).map(h => h.id);
      
      if (houseIds.length === 0) {
        console.log('✅ No houses found for streets');
        setStreets(streetsData.map(s => ({
          ...s,
          tag_counts: { BK: 0, NA: 0, HOT: 0, KT: 0, KI: 0, NO: 0, WIN: 0 },
          total_units: 0
        })));
        return;
      }

      // Finally load only unit tags and status - minimal data
      const { data: unitsData, error: unitsError } = await supabase
        .from('d2d_units')
        .select('id, tags, status, house_id')
        .in('house_id', houseIds);

      if (unitsError) {
        console.error('Error loading units:', unitsError);
      }

      // Build a map of house_id to street_id for faster lookup
      const houseToStreet = new Map(
        (housesData || []).map(h => [h.id, h.street_id])
      );

      // Build tag counts per street
      const streetCounts = new Map<string, { tags: any, total: number }>();
      
      (unitsData || []).forEach(unit => {
        const streetId = houseToStreet.get(unit.house_id);
        if (!streetId || unit.status !== 'active' && unit.status) return;
        
        if (!streetCounts.has(streetId)) {
          streetCounts.set(streetId, {
            tags: { BK: 0, NA: 0, HOT: 0, KT: 0, KI: 0, NO: 0, WIN: 0 },
            total: 0
          });
        }
        
        const counts = streetCounts.get(streetId)!;
        counts.total++;
        
        (unit.tags || []).forEach((tag: string) => {
          if (tag in counts.tags) {
            counts.tags[tag]++;
          }
        });
      });

      const streetsWithTagCounts = streetsData.map(street => {
        const counts = streetCounts.get(street.id);
        return {
          ...street,
          tag_counts: counts?.tags || { BK: 0, NA: 0, HOT: 0, KT: 0, KI: 0, NO: 0, WIN: 0 },
          total_units: counts?.total || 0
        };
      });
      
      console.log('✅ Loaded streets:', streetsWithTagCounts.length);
      setStreets(streetsWithTagCounts);
    } catch (error) {
      console.error('❌ Error loading streets:', error);
      toast({
        title: "Fehler",
        description: "Straßen konnten nicht geladen werden.",
        variant: "destructive",
      });
      setStreets([]); // Clear streets on error
    } finally {
      setLoading(false);
    }
  };

  // Load houses for street (including shared areas)
  const loadHouses = async (streetId: string) => {
    if (!user?.id) return;
    
    // Set current street ID for realtime sync
    setCurrentStreetId(streetId);
    setCurrentHouseId(null);
    
    setLoading(true);
    try {
      // If offline, try to use cached data
      if (!isOnline) {
        const cachedHouses = await getCachedHouses(streetId);
        if (cachedHouses.length > 0) {
          console.log('📦 Using cached houses:', cachedHouses.length);
          setHouses(cachedHouses);
          setLoading(false);
          return;
        }
      }
      
      // Load all houses with units and house notes - RLS policies handle access control
      const { data, error } = await supabase
        .from('d2d_houses')
        .select(`
          *,
          d2d_units(
            *,
            d2d_notes(*)
          ),
          d2d_house_notes(*)
        `)
        .eq('street_id', streetId)
        .order('house_number');

      if (error) throw error;
      
      const housesWithUnits = (data || []).map(house => {
        const processedUnits = (house.d2d_units || []).map(unit => ({
          ...unit,
          notes: unit.d2d_notes || [],
          notes_count: unit.d2d_notes?.length || 0
        }));
        
        return {
          ...house,
          units: processedUnits,
          tag_summary: generateTagSummary(house.d2d_units || []),
          notes: house.d2d_house_notes || [],
          notes_count: house.d2d_house_notes?.length || 0
        };
      });
      
      setHouses(housesWithUnits);
    } catch (error) {
      console.error('Error loading houses:', error);
      toast({
        title: "Fehler",
        description: "Hausnummern konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load units for house (including shared areas)
  const loadUnits = async (houseId: string) => {
    if (!user?.id) return;
    
    // Set current house ID for realtime sync
    setCurrentHouseId(houseId);
    
    setLoading(true);
    try {
      // If offline, try to use cached data
      if (!isOnline) {
        const cachedUnits = await getCachedUnits(houseId);
        if (cachedUnits.length > 0) {
          console.log('📦 Using cached units:', cachedUnits.length);
          setUnits(cachedUnits);
          setLoading(false);
          return;
        }
      }
      
      // Load all units - RLS policies handle access control
      const { data, error } = await supabase
        .from('d2d_units')
        .select(`
          *,
          d2d_notes(*)
        `)
        .eq('house_id', houseId)
        .order('created_at');

      if (error) throw error;
      
      const unitsWithData = (data || []).map(unit => ({
        ...unit,
        notes: unit.d2d_notes || [],
        notes_count: unit.d2d_notes?.length || 0,
        is_hot: isHotLead(unit)
      }));
      
      setUnits(unitsWithData);
    } catch (error) {
      console.error('Error loading units:', error);
      toast({
        title: "Fehler",
        description: "Wohneinheiten konnten nicht geladen werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Create street
  const createStreet = async (areaId: string, streetData: D2DCreateStreetData) => {
    if (!user?.id) return null;

    try {
      // Get user's organization_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      const { data, error } = await supabase
        .from('d2d_streets')
        .insert({
          area_id: areaId,
          user_id: user.id,
          organization_id: profile?.organization_id || user.id,
          name: streetData.name,
          plz: streetData.plz,
        })
        .select()
        .single();

      if (error) throw error;

      // Track this item to prevent duplicate UI updates from realtime
      addRecentlyCreatedItem(data.id);

      // Save PLZ as last used
      await savePreferences({ last_plz: streetData.plz });

      toast({
        title: "Straße erstellt",
        description: `${streetData.name} wurde erfolgreich angelegt.`,
      });

      // Update local state
      setStreets(prev => [...prev, data]);

      return data;
    } catch (error) {
      console.error('Error creating street:', error);
      toast({
        title: "Fehler",
        description: "Straße konnte nicht erstellt werden.",
        variant: "destructive",
      });
      return null;
    }
  };

  // Create house with first unit
  const createHouse = async (streetId: string, houseData: D2DCreateHouseData) => {
    if (!user?.id) return null;

    try {
      // Get user's organization_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      // Check if first unit has any meaningful data (name or tags)
      const hasUnitData = (houseData.first_unit.unit_name && houseData.first_unit.unit_name.trim()) || 
                          (houseData.first_unit.tags && houseData.first_unit.tags.length > 0);

      // First create the house
      // IMPORTANT: Keep house creation resilient. If unit creation fails, the house should still exist.
      let { data: house, error: houseError } = await supabase
        .from('d2d_houses')
        .insert({
          street_id: streetId,
          user_id: user.id,
          organization_id: profile?.organization_id || user.id,
          house_number: houseData.house_number,
          // Start at 0; we'll increment after the unit is actually created.
          unit_count: 0,
        })
        .select()
        .single();

      if (houseError) throw houseError;
      
      // Track this item to prevent duplicate UI updates from realtime
      addRecentlyCreatedItem(house.id);

      let unit = null;
      let unitCreationFailed = false;

      // Only create a unit if it has meaningful data (name or tags)
      if (hasUnitData) {
        try {
        // Calculate reminder if NA tag is selected or if custom reminder is provided
        let reminderTime = null;
        let reminderReason = null;
        
        if (houseData.first_unit.tags.includes('NA')) {
          // Use new smart follow-up scheduling for NA tags - will be set after unit creation
          reminderTime = null;
          reminderReason = 'na_followup';
        } else if (houseData.first_unit.reminder) {
          // Handle custom reminder (e.g., KT follow-up)
          try {
            const reminderData = JSON.parse(houseData.first_unit.reminder);
            reminderTime = reminderData.date;
            reminderReason = 'kt_followup';
          } catch (error) {
            // Fallback: treat as direct reminder string
            reminderTime = houseData.first_unit.reminder;
          }
        }

          // Then create the first unit
          const { data: unitData, error: unitError } = await supabase
          .from('d2d_units')
          .insert({
            house_id: house.id,
            user_id: user.id,
            organization_id: profile?.organization_id || user.id,
            unit_name: houseData.first_unit.unit_name,
            tags: houseData.first_unit.tags,
            last_contact_at: new Date().toISOString(),
            next_reminder: reminderTime,
            reminder_reason: reminderReason,
          })
          .select()
          .single();

          if (unitError) throw unitError;
          unit = unitData;
          
          // Track this item to prevent duplicate UI updates from realtime
          addRecentlyCreatedItem(unit.id);

          // Update house unit_count now that a unit exists (best-effort)
          try {
            const { error: houseCountError } = await supabase
              .from('d2d_houses')
              .update({ unit_count: 1 })
              .eq('id', house.id);

            if (houseCountError) {
              console.warn('⚠️ House unit_count update failed (ignored):', houseCountError);
            } else {
              house = { ...house, unit_count: 1 } as any;
            }
          } catch (e) {
            console.warn('⚠️ House unit_count update exception (ignored):', e);
          }

        // Handle NA tag with smart scheduling after unit creation
        if (houseData.first_unit.tags.includes('NA')) {
          try {
            console.log('🚀 Calling smart-followup-scheduling for new unit with NA tag:', unit.id);
            
            const { data: smartData, error: smartError } = await supabase.functions.invoke('smart-followup-scheduling', {
              body: {
                unit_id: unit.id,
                visit_result: 'NA'
              }
            });

            if (smartError) {
              console.error('❌ Error in smart follow-up scheduling for new unit:', smartError);
            } else {
              console.log('✅ Smart follow-up scheduled for new unit:', smartData);
            }
          } catch (error) {
            console.error('❌ Error calling smart follow-up scheduling:', error);
          }
        }

        // Add note if provided - with retry logic for mobile reliability
        if (houseData.first_unit.note && houseData.first_unit.note.trim()) {
          let noteInserted = false;
          let noteAttempts = 0;
          const maxNoteAttempts = 2;
          
          while (!noteInserted && noteAttempts < maxNoteAttempts) {
            noteAttempts++;
            try {
              const { error: noteError } = await supabase
                .from('d2d_notes')
                .insert({
                  unit_id: unit.id,
                  user_id: user.id,
                  note: houseData.first_unit.note.trim(),
                });
              
              if (noteError) throw noteError;
              noteInserted = true;
              console.log('✅ Note added for new house unit on attempt', noteAttempts);
            } catch (noteErr) {
              console.warn(`⚠️ Note insert attempt ${noteAttempts} failed:`, noteErr);
              if (noteAttempts >= maxNoteAttempts) {
                console.error('❌ Failed to add note after retries, continuing without note');
              } else {
                // Wait before retry
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            }
          }
        }
        } catch (unitErr) {
          unitCreationFailed = true;
          console.error('Error creating first unit for new house (house kept):', unitErr);

          // Best-effort: ensure unit_count stays consistent
          try {
            await supabase.from('d2d_houses').update({ unit_count: 0 }).eq('id', house.id);
          } catch {
            // ignore
          }
        }
      }

      toast({
        title: "Hausnummer erstellt",
        description: unitCreationFailed
          ? `Hausnummer ${houseData.house_number} wurde angelegt, aber die erste Wohneinheit konnte nicht erstellt werden.`
          : hasUnitData 
            ? `Hausnummer ${houseData.house_number} mit Wohneinheit wurde erfolgreich angelegt.`
            : `Hausnummer ${houseData.house_number} wurde erfolgreich angelegt.`,
      });

      // Update local state with house and its first unit (if created)
      if (unit) {
        // Prepare the unit with notes for local state
        const noteArray = houseData.first_unit.note ? [{
          id: 'temp-' + Date.now(),
          unit_id: unit.id,
          user_id: user.id,
          note: houseData.first_unit.note,
          created_at: new Date().toISOString()
        }] : [];

        const houseWithUnit = {
          ...house,
          units: [{
            ...unit,
            notes: noteArray,
            notes_count: noteArray.length,
            is_hot: isHotLead(unit)
          }],
          tag_summary: generateTagSummary([unit])
        };

        setHouses(prev => [...prev, houseWithUnit]);
      } else {
        // House without unit
        const houseWithoutUnit = {
          ...house,
          units: [],
          tag_summary: ''
        };

        setHouses(prev => [...prev, houseWithoutUnit]);
      }

      return house;
    } catch (error) {
      console.error('Error creating house:', error);
      toast({
        title: "Fehler",
        description: "Hausnummer konnte nicht erstellt werden.",
        variant: "destructive",
      });
      return null;
    }
  };

  // Use optimistic create from useUnitManagement
  const createUnit = createUnitOptimistic;
  const createUnitsBatch = createUnitsBatchOptimistic;

  // Create follow-up for unit
  const createUnitFollowUp = async (unitId: string, tag: D2DTag, reminderDate: Date, notes?: string, isManual?: boolean, customerEmail?: string) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase.rpc('create_unit_follow_up', {
        unit_id_param: unitId,
        tag_param: tag,
        reminder_date_param: reminderDate.toISOString(),
        follow_up_notes_param: notes || null,
        is_manual_param: isManual || false
      });

      if (error) throw error;

      // Save customer email if provided
      // Note: RLS handles access control, no user_id filter needed (supports shared areas)
      if (customerEmail && customerEmail.trim()) {
        const { error: emailError } = await supabase
          .from('d2d_units')
          .update({ customer_email: customerEmail.trim() })
          .eq('id', unitId);
        
        if (emailError) {
          console.error('Error saving customer email:', emailError);
        }
      }

      // Email sending is handled below in the unified block (line 610+)

      // Update local state immediately for instant UI feedback
      const updateData = {
        next_reminder: reminderDate.toISOString(),
        reminder_reason: tag,
        follow_up_notes: notes || null,
        manual_reminder: isManual || false,
        last_contact_at: new Date().toISOString(),
        ...(customerEmail && customerEmail.trim() && { customer_email: customerEmail.trim() })
      };

      setUnits(prev => prev.map(unit => 
        unit.id === unitId 
          ? { ...unit, ...updateData }
          : unit
      ));

      // Also update houses state if the unit is nested there
      setHouses(prev => prev.map(house => ({
        ...house,
        units: house.units?.map(unit => 
          unit.id === unitId 
            ? { ...unit, ...updateData }
            : unit
        )
      })));

      // If notes were provided, also add them as a permanent note to d2d_notes
      if (notes && notes.trim()) {
        const { error: notesError } = await supabase
          .from('d2d_notes')
          .insert({
            unit_id: unitId,
            user_id: user.id,
            note: notes.trim()
          });

        if (notesError) {
          console.error('Error adding note:', notesError);
        }
      }

      // Send appointment email for all follow-ups
      try {
        console.log('📧 Sending appointment email for follow-up (manual:', isManual, ')');
        
        // Get user email
        const { data: profile } = await supabase
          .from('profiles')
          .select('email')
          .eq('user_id', user.id)
          .single();

        // Get unit data to check for customer
        const { data: unitData } = await supabase
          .from('d2d_units')
          .select('customer_id')
          .eq('id', unitId)
          .single();

        let finalCustomerEmail: string | undefined = customerEmail;
        
        // If no email provided but unit has a customer, get customer email from database
        if (!finalCustomerEmail && unitData?.customer_id) {
          const { data: customerData } = await supabase
            .from('customers')
            .select('email')
            .eq('id', unitData.customer_id)
            .single();
          
          finalCustomerEmail = customerData?.email || undefined;
        }

        const userEmail = profile?.email || user.email;

        if (userEmail) {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            await supabase.functions.invoke('send-appointment-email', {
              body: {
                unit_id: unitId,
                appointment_date: reminderDate.toISOString(),
                notes: notes,
                user_email: userEmail,
                customer_email: finalCustomerEmail
              },
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              }
            });

            console.log('✅ Appointment email sent successfully');
          }
        }
      } catch (emailError) {
        console.error('⚠️ Error sending appointment email:', emailError);
        // Don't fail the whole operation if email fails
      }

      console.log('✅ Follow-up created in database and local state');

      toast({
        title: "Follow-Up erstellt",
        description: `Follow-Up für ${format(reminderDate, "dd.MM.yyyy HH:mm", { locale: de })} wurde erstellt.`,
      });
    } catch (error) {
      console.error('Error creating follow-up:', error);
      toast({
        title: "Fehler",
        description: "Follow-Up konnte nicht erstellt werden.",
        variant: "destructive",
      });
    }
  };

  // Update unit name
  const updateUnitName = async (unitId: string, name: string) => {
    if (!user?.id) return;

    // If offline, queue the action
    if (!isOnline) {
      queueAction('update_name', { unitId, name });
      
      // Update local state optimistically
      setUnits(prev => prev.map(unit => 
        unit.id === unitId 
          ? { ...unit, unit_name: name.trim() || null, offline_sync_pending: true }
          : unit
      ));
      return;
    }

    try {
      const { error } = await supabase
        .from('d2d_units')
        .update({ 
          unit_name: name.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', unitId);

      if (error) throw error;

      // Update local state immediately for instant UI feedback
      setUnits(prev => prev.map(unit => 
        unit.id === unitId 
          ? { ...unit, unit_name: name.trim() || null }
          : unit
      ));

      // Also update houses state if the unit is nested there
      setHouses(prev => prev.map(house => ({
        ...house,
        units: house.units?.map(unit => 
          unit.id === unitId 
            ? { ...unit, unit_name: name.trim() || null }
            : unit
        )
      })));

      console.log('✅ Unit name updated in database and local state');

      toast({
        title: "Erfolg",
        description: "Name wurde aktualisiert.",
      });
    } catch (error) {
      console.error('Error updating unit name:', error);
      toast({
        title: "Fehler",
        description: "Name konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  };

  // ============================================================================
  // CRITICAL: Update unit tags - SINGLE SOURCE OF TRUTH
  // This is the ONLY function that should update tags in the database.
  // It uses optimistic UI updates for instant feedback.
  // ============================================================================
  const updateUnitTagsOnly = async (unitId: string, tags: D2DTag[]) => {
    if (!user?.id) return;

    const normalizeTags = (input: D2DTag[]) => {
      const seen = new Set<D2DTag>();
      const out: D2DTag[] = [];
      for (const t of input) {
        if (!seen.has(t)) {
          seen.add(t);
          out.push(t);
        }
      }
      return out;
    };

    const normalizedTags = normalizeTags(tags);

    // Get current unit from local state for comparison (no DB fetch needed)
    const currentLocalUnit = units.find(u => u.id === unitId);
    const currentTags = (currentLocalUnit?.tags || []) as D2DTag[];
    const currentContactAttempts = currentLocalUnit?.contact_attempts_count || 0;
    const currentReminder = currentLocalUnit?.next_reminder;

    // Calculate what needs to change
    const hadNA = currentTags.includes('NA');
    const hadKT = currentTags.includes('KT');
    const hadWIN = currentTags.includes('WIN');
    const hadKI = currentTags.includes('KI');
    const hadNO = currentTags.includes('NO');
    const hasKT = normalizedTags.includes('KT');
    const hasNA = normalizedTags.includes('NA');
    const hasWIN = normalizedTags.includes('WIN');
    const hasKI = normalizedTags.includes('KI');
    const hasNO = normalizedTags.includes('NO');

    const naAdded = !hadNA && hasNA;
    const winAdded = !hadWIN && hasWIN;
    const kiAdded = !hadKI && hasKI;
    const noAdded = !hadNO && hasNO;
    const isNAtoKTSwitch = hadNA && hasKT && !hadKT;
    const isKTtoNASwitch = hadKT && hasNA && !hasKT;

    // Check if KT -> NA switch on day of planned follow-up AND after follow-up time
    const reminderDate = currentReminder ? new Date(currentReminder) : null;
    const now = new Date();
    const isSameDayAsReminder = reminderDate &&
      reminderDate.getDate() === now.getDate() &&
      reminderDate.getMonth() === now.getMonth() &&
      reminderDate.getFullYear() === now.getFullYear();
    const isAfterReminderTime = reminderDate && now >= reminderDate;

    const shouldSendMissedVisitEmail = isKTtoNASwitch && isSameDayAsReminder && isAfterReminderTime;
    const shouldClearFollowUp = winAdded || kiAdded || noAdded;
    const shouldClearLastContact = kiAdded || noAdded;

    const newContactAttempts = naAdded ? currentContactAttempts + 1 :
                               shouldClearLastContact ? 0 : currentContactAttempts;

    // CRITICAL: Optimistic UI update IMMEDIATELY - before any DB calls
    const updateData: Partial<D2DUnit> = {
      tags: [...normalizedTags],
      last_contact_at: shouldClearLastContact ? undefined : new Date().toISOString(),
    };

    if (naAdded || shouldClearLastContact) {
      updateData.contact_attempts_count = newContactAttempts;
    }

    if (shouldClearFollowUp) {
      updateData.next_reminder = undefined;
      updateData.reminder_reason = undefined;
      updateData.follow_up_notes = undefined;
    }

    setUnits(prev => prev.map(unit =>
      unit.id === unitId
        ? { ...unit, ...updateData, is_hot: isHotLead({ ...unit, ...updateData } as D2DUnit) }
        : unit
    ));
    console.log('✅ Unit tags updated with optimistic update', { tags: normalizedTags, winAdded, kiAdded, noAdded });

    // Now do DB operations
    try {
      // If switching from NA to KT, clear system notes in background
      if (isNAtoKTSwitch) {
        (async () => {
          try {
            await supabase
              .from('d2d_notes')
              .delete()
              .eq('unit_id', unitId)
              .eq('user_id', user.id)
              .ilike('note', '🤖%');
            console.log('✅ System notes cleared');
          } catch (err) {
            console.error('❌ Error clearing notes:', err);
          }
        })();
      }

      const updatePayload: any = {
        tags: [...normalizedTags],
        updated_at: new Date().toISOString(),
      };

      // Only set last_contact_at if not clearing it
      if (!shouldClearLastContact) {
        updatePayload.last_contact_at = new Date().toISOString();
      }

      if (naAdded) {
        updatePayload.contact_attempts_count = newContactAttempts;
      }

      // Generate new appointment token when KT is removed
      if (hadKT && !normalizedTags.includes('KT')) {
        updatePayload.appointment_token = crypto.randomUUID();
        console.log('🔐 Generated new appointment token because KT was removed');
      }

      // Clear follow-up data for WIN/KI/NO tags
      if (shouldClearFollowUp) {
        updatePayload.next_reminder = null;
        updatePayload.reminder_reason = null;
        updatePayload.follow_up_notes = null;
        console.log('🗑️ Clearing follow-up data for WIN/KI/NO tag');
      }

      // Clear last contact for KI/NO tags
      if (shouldClearLastContact) {
        updatePayload.last_contact_at = null;
        updatePayload.contact_attempts_count = 0;
        console.log('🗑️ Clearing last contact data for KI/NO tag');
      }

      const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
        return new Promise<T>((resolve, reject) => {
          const id = window.setTimeout(() => reject(new Error('timeout')), ms);
          promise
            .then((value) => {
              window.clearTimeout(id);
              resolve(value);
            })
            .catch((err) => {
              window.clearTimeout(id);
              reject(err);
            });
        });
      };

      const runDbUpdateOnce = async () => {
        const { error } = await supabase
          .from('d2d_units')
          .update(updatePayload)
          .eq('id', unitId);

        if (error) throw error;
      };

      const maxAttempts = 3;
      const timeoutMs = 8000;

      const isTimeoutLike = (e: any) => e?.code === '57014' || e?.message === 'timeout';

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          await withTimeout(runDbUpdateOnce(), timeoutMs);
          break;
        } catch (err) {
          const isLast = attempt === maxAttempts;
          console.warn('⚠️ Tag DB update attempt failed', { attempt, unitId, err });

          // Retry only for statement timeouts / hung requests
          if (!isTimeoutLike(err) || isLast) throw err;

          await new Promise((r) => window.setTimeout(r, 700 * attempt));
        }
      }

      console.log('✅ Tags saved to database');

      // Send missed visit email AFTER successful DB update
      if (shouldSendMissedVisitEmail) {
        console.log('🚨 KT -> NA switch detected on reminder day, sending missed visit email');
        supabase.functions.invoke('send-missed-visit-email', {
          body: { unit_id: unitId }
        }).then(({ error: emailError }) => {
          if (emailError) {
            console.error('❌ Error sending missed visit email:', emailError);
          } else {
            console.log('✅ Missed visit email sent successfully');
            toast({
              title: "E-Mails versendet",
              description: "Kunde und Berater wurden über den verpassten Termin informiert.",
            });
          }
        }).catch(error => console.error('❌ Error invoking send-missed-visit-email:', error));
      }

      if (naAdded) {
        console.log('ℹ️ NA tag added - recording contact attempt in history');
        // Record in d2d_contact_attempts for history tracking
        supabase
          .from('d2d_contact_attempts')
          .insert({
            unit_id: unitId,
            user_id: user.id,
            result_tag: 'NA',
          })
          .then(({ error: attemptError }) => {
            if (attemptError) console.error('❌ Error recording contact attempt:', attemptError);
            else console.log('✅ Contact attempt recorded in history: NA');
          });
      }
    } catch (error) {
      console.error('Error updating unit tags:', error);
      // Revert optimistic update on error
      setUnits(prev => prev.map(unit => 
        unit.id === unitId 
          ? { ...unit, tags: currentTags, contact_attempts_count: currentContactAttempts }
          : unit
      ));
      throw error;
    }
  };

  // ============================================================================
  // CRITICAL: Update unit tags with automatic follow-up logic
  // IMPORTANT: This function includes IMMEDIATE local state updates for instant UI feedback
  // DO NOT remove the optimistic state updates - they are essential for UX
  // Realtime subscriptions serve as backup/sync mechanism only
  // ============================================================================
  const updateUnitTags = async (unitId: string, tags: D2DTag[]) => {
    if (!user?.id) return;

    // If offline, queue the action
    if (!isOnline) {
      queueAction('update_tags', { unitId, tags });
      
      // Update local state optimistically
      setUnits(prev => prev.map(unit => 
        unit.id === unitId 
          ? { ...unit, tags, offline_sync_pending: true, last_contact_at: new Date().toISOString() }
          : unit
      ));
      return;
    }

    try {
      // First, get current tags to detect NA to KT switch
      // Note: RLS handles access control, no user_id filter needed (supports shared areas)
      const { data: currentUnit, error: fetchError } = await supabase
        .from('d2d_units')
        .select('tags')
        .eq('id', unitId)
        .single();

      if (fetchError) throw fetchError;

      const currentTags = currentUnit?.tags as D2DTag[] || [];
      const hadNA = currentTags.includes('NA');
      const hasNA = tags.includes('NA');
      const naAdded = !hadNA && hasNA;
      const hasKT = tags.includes('KT');
      const hadKT = currentTags.includes('KT');
      const isNAtoKTSwitch = hadNA && hasKT && !hadKT;
      const isKTtoNASwitch = hadKT && hasNA && !currentTags.includes('NA');

      // Use tags as provided by D2DTagSelector (already validated)
      const finalTags = [...tags];

      // If switching from NA to KT, clear existing notes
      if (isNAtoKTSwitch) {
        await supabase
          .from('d2d_notes')
          .delete()
          .eq('unit_id', unitId)
          .eq('user_id', user.id);
      }

      // Only increment contact attempts if NA tag is being added
      let newContactAttempts = 0;
      if (naAdded) {
        // Get current contact attempts count to increment it
        const { data: currentData, error: fetchError2 } = await supabase
          .from('d2d_units')
          .select('contact_attempts_count')
          .eq('id', unitId)
          .single();

        if (fetchError2) throw fetchError2;
        newContactAttempts = (currentData?.contact_attempts_count || 0) + 1;
      }

      // CRITICAL: Always update tags in database, even if NA was just added
      // This ensures BK, HOT, and other tags are properly saved
      const updatePayload: any = {
        tags: finalTags,
        last_contact_at: new Date().toISOString(),
      };

      if (naAdded) {
        updatePayload.contact_attempts_count = newContactAttempts;
      }

      // 🔐 SECURITY: Generate new appointment token when KT is removed
      // This invalidates old booking/cancellation links for security
      if (hadKT && !finalTags.includes('KT')) {
        updatePayload.appointment_token = crypto.randomUUID();
        console.log('🔐 Generated new appointment token because KT was removed');
      }

      const { error } = await supabase
        .from('d2d_units')
        .update(updatePayload)
        .eq('id', unitId);

      if (error) throw error;

      console.log('✅ Tags updated in database', { finalTags, naAdded, ktRemoved: hadKT && !finalTags.includes('KT') });

      // KT tags should NOT get automatic reminders - only manual ones through the popup

      // Remove reminder for KI tags
      if (finalTags.includes('KI')) {
        await supabase
          .from('d2d_units')
          .update({
            next_reminder: null,
            reminder_reason: null,
          })
          .eq('id', unitId);
      }

      // Remove reminder for NO tags - exclude from follow-up process
      if (finalTags.includes('NO')) {
        await supabase
          .from('d2d_units')
          .update({
            next_reminder: null,
            reminder_reason: null,
          })
          .eq('id', unitId);
      }

      // CRITICAL: Optimistic UI update for instant feedback
      const updateData: Partial<D2DUnit> = {
        tags: finalTags,
        last_contact_at: new Date().toISOString(),
        ...(finalTags.includes('KI') || finalTags.includes('NO') ? { next_reminder: null, reminder_reason: null } : {})
      };
      
      if (naAdded) {
        updateData.contact_attempts_count = newContactAttempts;
      }

      setUnits(prev => prev.map(unit => 
        unit.id === unitId 
          ? { ...unit, ...updateData, is_hot: isHotLead({ ...unit, ...updateData } as D2DUnit) }
          : unit
      ));

      console.log('✅ Tags updated with optimistic update');

      // Handle NA tag with smart scheduling
      // NOTE: NA smart scheduling is now triggered centrally via onNAFollowUp in D2DTagSelector
      if (naAdded) {
        console.log('ℹ️ NA tag added - recording contact attempt in history');
        supabase
          .from('d2d_contact_attempts')
          .insert({
            unit_id: unitId,
            user_id: user.id,
            result_tag: 'NA',
          })
          .then(({ error: attemptError }) => {
            if (attemptError) console.error('❌ Error recording contact attempt:', attemptError);
            else console.log('✅ Contact attempt recorded in history: NA');
          });
      }
 
    } catch (error) {
      console.error('Error updating unit tags:', error);
      toast({
        title: "Fehler",
        description: "Tags konnten nicht aktualisiert werden.",
        variant: "destructive",
      });
    }
  };

  // Note: addNote and deleteNote are now handled by useUnitManagement hook
  const addNote = addNoteOptimistic;
  const deleteNote = deleteNoteOptimistic;

  // Utility functions
  const generateTagSummary = (units: D2DUnit[]) => {
    const counts = { BK: 0, NA: 0, HOT: 0, KT: 0, KI: 0, NO: 0, WIN: 0 };
    units.forEach(unit => {
      unit.tags.forEach(tag => {
        if (tag in counts) {
          counts[tag as D2DTag]++;
        }
      });
    });

    const summary = Object.entries(counts)
      .filter(([_, count]) => count > 0)
      .map(([tag, count]) => `${count} ${tag}`)
      .join(', ');

    return summary || 'Keine Tags';
  };

  // REMOVED: Duplicate isHotLead function - using the one at line 29 instead



  // Delete a single unit
  const deleteUnit = async (unitId: string) => {
    if (!user?.id) return;

    try {
      // First delete all notes associated with this unit
      const { error: notesError } = await supabase
        .from('d2d_notes')
        .delete()
        .eq('unit_id', unitId)
        .eq('user_id', user.id);

      if (notesError) throw notesError;

      // Then delete the unit itself
      const { error: deleteError } = await supabase
        .from('d2d_units')
        .delete()
        .eq('id', unitId)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      // Update local state - remove the unit from the units array
      setUnits(prev => prev.filter(unit => unit.id !== unitId));

      // Also update houses state to decrement unit count
      setHouses(prev => prev.map(house => {
        if (house.units?.some(unit => unit.id === unitId)) {
          return {
            ...house,
            unit_count: Math.max(0, house.unit_count - 1),
            units: house.units?.filter(unit => unit.id !== unitId)
          };
        }
        return house;
      }));

      toast({
        title: "Wohneinheit gelöscht",
        description: "Die Wohneinheit wurde erfolgreich gelöscht.",
      });

    } catch (error) {
      console.error('Error deleting unit:', error);
      toast({
        title: "Fehler",
        description: "Wohneinheit konnte nicht gelöscht werden.",
        variant: "destructive",
      });
      // Don't throw the error - it's already handled and shown to the user
    }
  };

  // Delete a house and all its units
  const deleteHouse = async (houseId: string) => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // First delete all notes associated with units in this house
      const { data: houseUnits, error: unitsError } = await supabase
        .from('d2d_units')
        .select('id')
        .eq('house_id', houseId)
        .eq('user_id', user.id);

      if (unitsError) throw unitsError;

      if (houseUnits && houseUnits.length > 0) {
        const unitIds = houseUnits.map(unit => unit.id);
        
        // Delete all notes for these units
        const { error: notesError } = await supabase
          .from('d2d_notes')
          .delete()
          .in('unit_id', unitIds)
          .eq('user_id', user.id);

        if (notesError) throw notesError;

        // Delete all units in this house
        const { error: deleteUnitsError } = await supabase
          .from('d2d_units')
          .delete()
          .eq('house_id', houseId)
          .eq('user_id', user.id);

        if (deleteUnitsError) throw deleteUnitsError;
      }

      // Finally delete the house
      const { error: deleteHouseError } = await supabase
        .from('d2d_houses')
        .delete()
        .eq('id', houseId)
        .eq('user_id', user.id);

      if (deleteHouseError) throw deleteHouseError;

      // Update local state
      setHouses(prevHouses => prevHouses.filter(house => house.id !== houseId));
      setUnits(prevUnits => prevUnits.filter(unit => unit.house_id !== houseId));

    } catch (error) {
      console.error('Error deleting house:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a street and all its houses and units with proper cascade
  const deleteStreet = async (streetId: string) => {
    if (!user?.id) return;

    try {
      setLoading(true);

      // First get all houses in this street (user-specific)
      const { data: streetHouses, error: housesError } = await supabase
        .from('d2d_houses')
        .select('id')
        .eq('street_id', streetId)
        .eq('user_id', user.id);

      if (housesError) throw housesError;

      if (streetHouses && streetHouses.length > 0) {
        const houseIds = streetHouses.map(house => house.id);
        
        // Get all units in these houses (user-specific)
        const { data: streetUnits, error: unitsError } = await supabase
          .from('d2d_units')
          .select('id')
          .in('house_id', houseIds)
          .eq('user_id', user.id);

        if (unitsError) throw unitsError;

        if (streetUnits && streetUnits.length > 0) {
          const unitIds = streetUnits.map(unit => unit.id);
          
          // Delete all notes for these units (user-specific)
          const { error: notesError } = await supabase
            .from('d2d_notes')
            .delete()
            .in('unit_id', unitIds)
            .eq('user_id', user.id);

          if (notesError) throw notesError;

          // Delete all units in these houses (user-specific)
          const { error: deleteUnitsError } = await supabase
            .from('d2d_units')
            .delete()
            .in('house_id', houseIds)
            .eq('user_id', user.id);

          if (deleteUnitsError) throw deleteUnitsError;
        }

        // Delete all houses in this street (user-specific)
        const { error: deleteHousesError } = await supabase
          .from('d2d_houses')
          .delete()
          .eq('street_id', streetId)
          .eq('user_id', user.id);

        if (deleteHousesError) throw deleteHousesError;
      }

      // Finally delete the street itself (user-specific)
      const { error: deleteStreetError } = await supabase
        .from('d2d_streets')
        .delete()
        .eq('id', streetId)
        .eq('user_id', user.id);

      if (deleteStreetError) throw deleteStreetError;

      // Update local state - remove all related data
      setStreets(prevStreets => prevStreets.filter(street => street.id !== streetId));
      setHouses(prevHouses => prevHouses.filter(house => house.street_id !== streetId));
      setUnits(prevUnits => prevUnits.filter(unit => {
        const house = houses.find(h => h.id === unit.house_id);
        return !house || house.street_id !== streetId;
      }));

    } catch (error) {
      console.error('Error deleting street:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Edit street name
  const editStreetName = async (streetId: string, newName: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('d2d_streets')
        .update({ 
          name: newName.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', streetId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setStreets(prevStreets => 
        prevStreets.map(street => 
          street.id === streetId 
            ? { ...street, name: newName.trim(), updated_at: new Date().toISOString() }
            : street
        )
      );

    } catch (error) {
      console.error('Error updating street name:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
      loadAreas();
    }
  }, [user?.id]);

  // House Notes Management
  const addHouseNote = async (houseId: string, noteText: string) => {
    if (!user?.id || !noteText.trim()) return;

    try {
      const { data, error } = await supabase
        .from('d2d_house_notes')
        .insert({
          house_id: houseId,
          user_id: user.id,
          note: noteText.trim()
        })
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setHouses(prevHouses => 
        prevHouses.map(house => 
          house.id === houseId 
            ? { 
                ...house, 
                notes: [...(house.notes || []), data],
                notes_count: (house.notes_count || 0) + 1
              }
            : house
        )
      );

      toast({
        title: "Erfolg",
        description: "Notiz wurde hinzugefügt.",
      });

      return data;
    } catch (error) {
      console.error('Error adding house note:', error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht hinzugefügt werden.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateHouseNote = async (noteId: string, noteText: string) => {
    if (!user?.id || !noteText.trim()) return;

    try {
      const { error } = await supabase
        .from('d2d_house_notes')
        .update({ note: noteText.trim() })
        .eq('id', noteId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setHouses(prevHouses => 
        prevHouses.map(house => ({
          ...house,
          notes: house.notes?.map(note => 
            note.id === noteId 
              ? { ...note, note: noteText.trim(), updated_at: new Date().toISOString() }
              : note
          )
        }))
      );

      toast({
        title: "Erfolg",
        description: "Notiz wurde aktualisiert.",
      });
    } catch (error) {
      console.error('Error updating house note:', error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht aktualisiert werden.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteHouseNote = async (houseId: string, noteId: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('d2d_house_notes')
        .delete()
        .eq('id', noteId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Update local state
      setHouses(prevHouses => 
        prevHouses.map(house => 
          house.id === houseId 
            ? { 
                ...house, 
                notes: house.notes?.filter(note => note.id !== noteId) || [],
                notes_count: Math.max(0, (house.notes_count || 1) - 1)
              }
            : house
        )
      );

      toast({
        title: "Erfolg",
        description: "Notiz wurde gelöscht.",
      });
    } catch (error) {
      console.error('Error deleting house note:', error);
      toast({
        title: "Fehler",
        description: "Notiz konnte nicht gelöscht werden.",
        variant: "destructive",
      });
      throw error;
    }
  };

  // Clear realtime context when navigating away
  const clearRealtimeContext = useCallback(() => {
    setCurrentAreaId(null);
    setCurrentStreetId(null);
    setCurrentHouseId(null);
  }, []);

  return {
    loading,
    isOnline,
    isSyncing,
    queueLength,
    areas,
    streets,
    houses,
    units,
    preferences,
    loadAreas,
    loadStreets,
    loadHouses,
    loadUnits,
    createStreet,
    createHouse,
    createUnit,
    createUnitsBatch,
    updateUnitTags,
    updateUnitTagsOnly,
    updateUnitName,
    createUnitFollowUp,
    addNote,
    deleteNote,
    deleteUnit,
    deleteHouse,
    deleteStreet,
    editStreetName,
    savePreferences,
    addHouseNote,
    updateHouseNote,
    deleteHouseNote,
    // Realtime sync context
    clearRealtimeContext,
    // Offline caching
    isCaching,
    cachedAreaIds,
    cacheArea,
    clearCache,
    checkAreaCached,
    getCachedAreaUnits,
    // Function to reorganize all follow-ups to prevent time conflicts
    reorganizeFollowups: async () => {
      if (!user?.id) return;

      try {
        console.log('🔄 Starting follow-up reorganization...');
        
        const { data, error } = await supabase.functions.invoke('reorganize-followups', {
          headers: {
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        });

        if (error) {
          console.error('❌ Error reorganizing follow-ups:', error);
          toast({
            title: "Fehler",
            description: "Follow-ups konnten nicht reorganisiert werden.",
            variant: "destructive",
          });
          throw error;
        }

        console.log('✅ Follow-ups reorganized successfully:', data);
        toast({
          title: "Erfolg",
          description: `${data.processed} Follow-ups wurden reorganisiert.`,
          variant: "default",
        });

        // Reload current data
        await loadAreas();
        
        return data;
      } catch (error) {
        console.error('❌ Error in reorganizeFollowups:', error);
        throw error;
      }
    },
    // Function to reorganize follow-ups for a specific area or street
    reorganizeFollowupsByLocation: async (areaId?: string, streetId?: string) => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase.functions.invoke('reorganize-followups', {
          body: { area_id: areaId, street_id: streetId },
          headers: {
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        });

        if (error) {
          toast({
            title: "Fehler",
            description: "Follow-ups konnten nicht reorganisiert werden.",
            variant: "destructive",
          });
          throw error;
        }

        const locationDesc = streetId ? 'der ausgewählten Straße' : areaId ? 'dem ausgewählten Gebiet' : 'allen Gebieten';
        
        toast({
          title: "Erfolg",
          description: `${data.processed} Follow-ups in ${locationDesc} wurden reorganisiert.`,
          variant: "default",
        });

        await loadAreas();
        
        return data;
      } catch (error) {
        console.error('Error in reorganizeFollowupsByLocation:', error);
        throw error;
      }
    },
  };
};